// src/app/services/analytics/google-connectors-api.ts
//
// The two read-only Google connectors (GA4, Search Console) behind the Outcomes
// view's demand series.
//
// The service account key travels in ONE direction only: from the paste field to
// POST /integrations/google/discover and POST /integrations/google/connect, over
// the existing authenticated fetch, and nowhere else. No endpoint here returns a
// key, nothing in this module stores one, and nothing caches one. Read the
// types: there is no field that could carry one back.
//
// Everything except the status read is admin only; the API enforces that and
// this module does not pretend otherwise.
import { apiFetch } from "@/api/api-fetch";

export type GoogleProvider = "ga4" | "gsc";

/** 'active' | 'error' | 'revoked' once connected, null when it never was. */
export type GoogleConnectionStatus = "active" | "error" | "revoked";

export interface Ga4Metadata {
  property_id?: string;
  property_display_name?: string;
  account_display_name?: string;
}

export interface GscMetadata {
  site_url?: string;
  /** e.g. siteRestrictedUser, siteFullUser, siteOwner. Kept verbatim. */
  permission_level?: string;
}

export type GoogleAccountMetadata = Ga4Metadata & GscMetadata;

export interface GoogleProviderStatus {
  provider: GoogleProvider;
  connected: boolean;
  /** The service account address. The one non-secret field of the key. */
  service_account_email: string | null;
  account_metadata: GoogleAccountMetadata;
  /** Search Console only. Matched locally, case-insensitive, substring. */
  brand_terms: string[];
  status: GoogleConnectionStatus | null;
  last_sync_at: string | null;
  last_error: string | null;
  /** Daily rows held. Distinguishes a connected-but-empty connector. */
  daily_row_count: number;
}

export interface GoogleStatusResponse {
  brand: string;
  providers: Record<GoogleProvider, GoogleProviderStatus>;
}

/** One GA4 property the granted service account can see. */
export interface Ga4PropertyOption {
  account_display_name: string;
  /** Numeric, without the "properties/" prefix. */
  property_id: string;
  property_display_name: string;
}

/** One Search Console property. site_url keeps its exact returned form, either
 *  `sc-domain:example.com` or `https://www.example.com/` — they are different
 *  properties covering different traffic. */
export interface GscSiteOption {
  site_url: string;
  permission_level: string;
}

export interface DiscoverResponse<T> {
  provider: GoogleProvider;
  service_account_email: string;
  items: T[];
}

export interface ConnectResponse extends GoogleProviderStatus {
  brand: string;
}

export interface SyncResponse extends GoogleProviderStatus {
  brand: string;
  result: {
    status: string;
    rows: number;
    window?: [string, string];
    error?: string;
  };
}

export interface DisconnectResponse extends GoogleProviderStatus {
  brand: string;
  deleted: boolean;
}

function qs(params: Record<string, string>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) search.set(key, value);
  return search.toString();
}

export const googleConnectorsApi = {
  /** Status for both providers. Readable by anyone assigned the brand. */
  async status(brand: string): Promise<GoogleStatusResponse> {
    return apiFetch<GoogleStatusResponse>(
      `/integrations/google?${qs({ brand })}`,
    );
  },

  /** Validate a key and list what it can see. Stores nothing. Admin only. */
  async discoverGa4(
    brand: string,
    serviceAccountJson: unknown,
  ): Promise<DiscoverResponse<Ga4PropertyOption>> {
    return apiFetch<DiscoverResponse<Ga4PropertyOption>>(
      "/integrations/google/discover",
      {
        method: "POST",
        body: JSON.stringify({
          brand,
          provider: "ga4",
          service_account_json: serviceAccountJson,
        }),
      },
    );
  },

  async discoverGsc(
    brand: string,
    serviceAccountJson: unknown,
  ): Promise<DiscoverResponse<GscSiteOption>> {
    return apiFetch<DiscoverResponse<GscSiteOption>>(
      "/integrations/google/discover",
      {
        method: "POST",
        body: JSON.stringify({
          brand,
          provider: "gsc",
          service_account_json: serviceAccountJson,
        }),
      },
    );
  },

  /**
   * Store the connection and run the first sync. Admin only.
   *
   * The API validates by one real read before writing anything, so a rejection
   * here means nothing was stored.
   */
  async connect(args: {
    brand: string;
    provider: GoogleProvider;
    serviceAccountJson: unknown;
    propertyId?: string;
    siteUrl?: string;
    brandTerms?: string[];
  }): Promise<ConnectResponse> {
    return apiFetch<ConnectResponse>("/integrations/google/connect", {
      method: "POST",
      body: JSON.stringify({
        brand: args.brand,
        provider: args.provider,
        service_account_json: args.serviceAccountJson,
        selection: {
          property_id: args.propertyId ?? null,
          site_url: args.siteUrl ?? null,
        },
        brand_terms: args.brandTerms ?? null,
      }),
    });
  },

  /** Run the sync now. Idempotent: upserts by date. Admin only. */
  async sync(provider: GoogleProvider, brand: string): Promise<SyncResponse> {
    return apiFetch<SyncResponse>(
      `/integrations/google/${provider}/sync?${qs({ brand })}`,
      { method: "POST" },
    );
  },

  /** Delete the connection and its daily rows. Admin only. */
  async disconnect(
    provider: GoogleProvider,
    brand: string,
  ): Promise<DisconnectResponse> {
    return apiFetch<DisconnectResponse>(
      `/integrations/google/${provider}?${qs({ brand })}`,
      { method: "DELETE" },
    );
  },
};

/** The label for a provider, in the one place that decides it. */
export function googleProviderLabel(provider: GoogleProvider): string {
  return provider === "ga4" ? "Google Analytics (GA4)" : "Google Search Console";
}

// ─── What a failed read means, in words ──────────────────────────────────────
//
// `last_error` is whatever Google said, which is a sentence written for whoever
// wrote the client library. An operator needs two things from it: what broke,
// and what to do next. So it is classified into exactly three cases, each with
// one plain title and one instruction, and the raw text stays available behind a
// disclosure rather than being thrown away — it is the only thing that helps
// when the classification turns out to be wrong.
//
// The rule lives HERE, not in the status block, because the same failure has to
// read the same way on the Integrations card as it does in the panel, and two
// copies of a rule are two rules.

export type GoogleErrorKind = "permission" | "credential" | "unknown";

export interface GoogleErrorCopy {
  kind: GoogleErrorKind;
  /** One plain sentence. What broke. */
  title: string;
  /** One instruction. What to do about it, naming the control that does it. */
  body: string;
}

/** Access to the property was taken away: the grant is gone, the key is fine. */
const PERMISSION_MARKS = ["permission", "denied", "does not have"];
/** The key itself no longer works: revoked, deleted, or its account disabled. */
const CREDENTIAL_MARKS = ["invalid_grant", "key", "revoked", "disabled account"];

/**
 * Classify a `last_error` into the case an operator can act on.
 *
 * Pure and total: every input lands in one of the three, and an unrecognised
 * error is "unknown" rather than a guess. Permission is tested first, because a
 * 403 that also mentions a key is still a grant problem and sending someone to
 * make a new key would waste the one they have.
 */
export function classifyGoogleError(lastError: string | null | undefined): GoogleErrorCopy {
  const text = (lastError || "").toLowerCase();
  const has = (marks: string[]) => marks.some((m) => text.includes(m));
  // Word-bounded, so an id that happens to contain 403 is not a permission error.
  const code = (n: string) => new RegExp(`\\b${n}\\b`).test(text);

  if (code("403") || has(PERMISSION_MARKS)) {
    return {
      kind: "permission",
      title: "Upmode's access to this property was removed.",
      body: "Grant the service account access again, then choose Reconnect.",
    };
  }
  if (code("401") || has(CREDENTIAL_MARKS)) {
    return {
      kind: "credential",
      title: "The key no longer works.",
      body: "Create a new key for the service account and choose Update key or property.",
    };
  }
  return {
    kind: "unknown",
    title: "The last read did not succeed.",
    body: "Choose Read now to try again. If it fails again, an Upmode administrator checks the Google project.",
  };
}

/** What a connected provider is bound to, for the status row. */
export function googleBoundTo(status: GoogleProviderStatus): string | null {
  const meta = status.account_metadata || {};
  if (status.provider === "ga4") {
    const id = meta.property_id;
    if (!id) return null;
    return meta.property_display_name
      ? `${meta.property_display_name} (${id})`
      : id;
  }
  return meta.site_url || null;
}
